import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import { AdminUserRow, AdminUsersListResponse, Competition } from '../types/interfaces';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

type HostPrivilege = 'basic' | 'admin';

interface AdminAddHostModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitions: Competition[];
  onSuccess: () => void;
}

const AdminAddHostModal: React.FC<AdminAddHostModalProps> = ({
  isOpen,
  onClose,
  competitions,
  onSuccess,
}) => {
  const [competitionId, setCompetitionId] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [privilege, setPrivilege] = useState<HostPrivilege>('basic');
  const [userQuery, setUserQuery] = useState('');
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const usersPageSize = 25;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setUsersError('');
    setToastMessage('');
    setUserQuery('');
    const firstComp = competitions?.[0];
    setCompetitionId(firstComp ? String(firstComp.id) : '');
    setUsername('');
    setPrivilege('basic');
    setUsersPage(1);
  }, [isOpen, competitions]);

  const loadUsers = async (page: number, q: string) => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await apiService.get('/users/list', {
        page,
        page_size: usersPageSize,
        q: q.trim() || undefined,
      });
      const data = (res.data || {}) as AdminUsersListResponse;
      const nextUsers = (data.users || []) as AdminUserRow[];
      setUsers(nextUsers);
      setUsersTotal(data.total || 0);
      setUsersPage(data.page || page);

      // Ensure the select always has a valid value.
      if (nextUsers.length > 0) {
        const hasSelected = username && nextUsers.some((u) => u.username === username);
        if (!hasSelected) setUsername(nextUsers[0].username);
      } else {
        setUsername('');
      }
    } catch (err: any) {
      console.error('Failed to load users for host picker', err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load users';
      setUsersError(String(msg));
      setUsers([]);
      setUsersTotal(0);
      setUsername('');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadUsers(1, userQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectedUserLabel = useMemo(() => {
    const u = users.find((x) => x.username === username);
    if (!u) return '';
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return `${name ? name + ' • ' : ''}${u.email}`;
  }, [users, username]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(usersTotal / usersPageSize));
  }, [usersTotal]);

  useEscapeToClose(onClose, { enabled: isOpen && !isLoading });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const compIdNum = Number(competitionId);
      if (!compIdNum) throw new Error('Select a competition');
      if (!username) throw new Error('Select a user');

      const token = authUtils.getStoredToken();
      await apiService.post('/hosts/add', {
        competition_id: compIdNum,
        username,
        privilege,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setToastMessage('Host added');
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to add host';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div
          className="register-modal admin-addhost-modal"
          role="dialog"
          aria-modal="true"
          style={{ width: 'min(900px, 96vw)', maxWidth: '900px' }}
        >
          <div className="register-modal__header">
            <h2 className="register-modal__title">Add Host</h2>
            <button
              type="button"
              className="register-modal__close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="register-modal__form" onSubmit={handleSubmit}>
            {error && (
              <div className="register-modal__error" role="alert">
                {error}
              </div>
            )}

            <div className="register-modal__fields">
              <label className="input-wrapper">
                <span className="input__label">Competition</span>
                <select
                  className="input input--default input--md"
                  value={competitionId}
                  onChange={(e) => setCompetitionId(e.target.value)}
                >
                  {competitions.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.title} (id={c.id})
                    </option>
                  ))}
                </select>
              </label>

              <Input
                name="userSearch"
                label="Search users"
                placeholder="Search by username, email, or name"
                value={userQuery}
                onChange={(e) => {
                  const next = e.target.value;
                  setUserQuery(next);
                  setUsersPage(1);
                  loadUsers(1, next);
                }}
                disabled={isLoading}
              />

              {usersError && (
                <div className="register-modal__error" role="alert">
                  {usersError}
                </div>
              )}

              <label className="input-wrapper">
                <span className="input__label">User</span>
                <select
                  className="input input--default input--md"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading || usersLoading || users.length === 0}
                >
                  {users.length === 0 ? (
                    <option value="" disabled>
                      No users found
                    </option>
                  ) : null}
                  {users.map((u) => {
                    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                    const label = `${u.username} • ${name || '-'} • ${u.email}`;
                    return (
                      <option key={u.id} value={u.username}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                {selectedUserLabel && (
                  <div className="input__helper">Selected: {selectedUserLabel}</div>
                )}

                <div className="input__helper">
                  {usersLoading ? (
                    'Loading users...'
                  ) : (
                    <>
                      Showing page {usersPage} of {totalPages} • Total users: {usersTotal}
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={usersLoading || usersPage <= 1 || isLoading}
                    onClick={() => {
                      const nextPage = Math.max(1, usersPage - 1);
                      setUsersPage(nextPage);
                      loadUsers(nextPage, userQuery);
                    }}
                  >
                    Prev
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={usersLoading || usersPage >= totalPages || isLoading}
                    onClick={() => {
                      const nextPage = Math.min(totalPages, usersPage + 1);
                      setUsersPage(nextPage);
                      loadUsers(nextPage, userQuery);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </label>

              <label className="input-wrapper">
                <span className="input__label">Privilege</span>
                <select
                  className="input input--default input--md"
                  value={privilege}
                  onChange={(e) => setPrivilege(e.target.value as HostPrivilege)}
                >
                  <option value="basic">basic</option>
                  <option value="admin">admin</option>
                </select>
              </label>
            </div>

            <div className="register-modal__actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                Add Host
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} duration={2500} onClose={() => setToastMessage('')} />
      )}
    </>
  );
};

export default AdminAddHostModal;
